import React, { useState, useEffect } from 'react';

import cx from 'classnames';

import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router';

import get from 'lodash.get';
import qs from 'query-string';

import OmView from '@xingternal/xing-view/OmView'; // eslint-disable-line

import Video from '@xingternal/video';
import { IconVideo } from '@xingternal/icons';

import { trackOpenGallery } from '@xingternal/pages-tracking';
import Gallery from '../../Gallery';
import GalleryEditButton from '../../Edit/GalleryEdit/GalleryEditButton';
import { GalleryItemsType } from '../../types/index';

import aboutUsImage from '../assets/about-us.svg'; // eslint-disable-line
import { SmallGrid, Carousel } from '@xingternal/pages-common';

import styles from './GalleryPreview.css';

type GalleryPreviewProps = {
  galleryItems: GalleryItemsType;
  pageSlug: string;
};

type GalleryItemsProps = {
  galleryItems: GalleryItemsType;
  itemClicked?: number | null;
  setItemClicked: (param: number) => void;
};

type ItemsWrapperProps = GalleryItemsProps & {
  activateOmView: () => void;
  setOmView: (param: any) => void;
};

type SliderItemsProps = GalleryItemsProps;

type MediaProps = {
  imageUrl?: string;
  videoId?: string;
  videoButtonSize: number;
  externalUrl?: string;
};

type ExternalIconProps = {
  small: boolean;
};

const ExternalIcon = ({ small }: ExternalIconProps) => (
  <div className={small ? styles.externalIconSmall : styles.externalIcon}>
    <IconVideo width={small ? 15 : 30} height={small ? 15 : 30} />
  </div>
);

const Media = ({
  imageUrl,
  videoId,
  videoButtonSize,
  externalUrl,
}: MediaProps) => (
  <>
    {videoId && (
      <Video
        id={videoId}
        onError={() => {}}
        previewModeAction={() => {}}
        previewModeButtonSize={videoButtonSize}
        section={'entity_pages_mainpage_preview_gallery'}
      />
    )}
    {externalUrl && <ExternalIcon small={videoButtonSize === 32} />}
    {imageUrl && (
      <>
        <div className={styles.blurredImageWrapper}>
          <div className={styles.gradient} />
          <div
            style={{ backgroundImage: `url(${imageUrl})` }}
            className={styles.blurredImage}
          />
        </div>
        <img src={imageUrl} className={styles.image} loading="lazy" alt="Gallery image" />
      </>
    )}
  </>
);

const GalleryItems = ({ galleryItems, setItemClicked }: GalleryItemsProps) => {
  const smallItems = galleryItems.slice(1);

  return (
    <>
      <div className={styles.galleryMediaMainWrapper}>
        <div
          onClick={() => setItemClicked(0)}
          className={styles.galleryMediaMain}
          data-testid="gallery-items-main"
        >
          <Media
            imageUrl={galleryItems[0].media?.url}
            videoId={galleryItems[0].media?.videoReferenceV2}
            externalUrl={galleryItems[0].media?.externalLink}
            videoButtonSize={64}
          />
        </div>
      </div>
      <div className={styles.smallItemsContainer}>
        {smallItems.map((item: any, index: number) => (
          <div
            key={index}
            onClick={() => setItemClicked(index + 1)}
            className={styles.galleryMedia}
            data-testid="gallery-items-small"
          >
            <Media
              imageUrl={item.media?.url}
              videoId={item.media?.videoReferenceV2}
              externalUrl={item.media?.externalLink}
              videoButtonSize={32}
            />
          </div>
        ))}
      </div>
    </>
  );
};

const SliderItems = ({ galleryItems, setItemClicked }: SliderItemsProps) => (
  <Carousel
    skin={{
      cardSnapStopAlignStart: styles.carousselItem,
      carouselMain: styles.carouselMain,
    }}
  >
    {galleryItems.map((item: any, index: number) => (
      <div key={index} className={styles.sliderItemWrapper}>
        <div
          onClick={() => setItemClicked(index)}
          className={styles.sliderItem}
        >
          <Media
            imageUrl={item.media?.url}
            videoId={item.media?.videoReferenceV2}
            videoButtonSize={64}
            externalUrl={item.media?.externalLink}
          />
        </div>
      </div>
    ))}
  </Carousel>
);

const ItemsWrapper = ({
  setOmView,
  activateOmView,
  ...props
}: ItemsWrapperProps) => {
  useEffect(() => setOmView(() => activateOmView));
  return (
    <>
      {/* Desktop displays a Gallery */}
      <div className={styles.galleryPreviewMediaWrapper}>
        <GalleryEditButton inFullWidth={true} />
        <GalleryItems {...props} />
      </div>
      {/* Mobile displays a Slider */}
      <div className={styles.sliderPreviewMediaWrapper}>
        <GalleryEditButton inFullWidth={true} />
        <SliderItems {...props} />
      </div>
    </>
  );
};

const GalleryPreview = ({ pageSlug, galleryItems }: GalleryPreviewProps) => {
  const location = useLocation();
  const history = useHistory();

  const openGallery =
    get(qs.parse(decodeURIComponent(location.search)), 'action') ===
    'openGallery';
  const item = get(qs.parse(decodeURIComponent(location.search)), 'item');

  const [itemClicked, setItemClicked] = useState<number | null>(
    openGallery && item ? parseInt(item) : null
  );
  const [isOmViewClosing, setIsOmViewClosing] = useState(false);

  const [omView, setOmView] = useState<null | (() => void)>(null);

  useEffect(() => {
    if (itemClicked !== null && omView) {
      trackOpenGallery('entity_page_mainpage');
      omView();
    }
  }, [itemClicked, omView]);

  const handleCloseOmView = () => {
    setIsOmViewClosing(true);
    setItemClicked(null);

    setTimeout(() => {
      history.replace({
        ...location,
        search: '',
      });
    }, 0);

    setTimeout(() => setIsOmViewClosing(false), 500);
  };

  return (
    <>
      {galleryItems?.length > 0 ? (
        <OmView
          onClose={() => handleCloseOmView()}
          trigger={(activateOmView: () => void) => (
            <ItemsWrapper
              galleryItems={galleryItems}
              itemClicked={itemClicked}
              setItemClicked={setItemClicked}
              activateOmView={activateOmView}
              setOmView={setOmView}
            />
          )}
        >
          <span
            className={cx({
              [styles.hideContent]: isOmViewClosing,
            })}
          >
            <SmallGrid>
              <Gallery pageSlug={pageSlug} itemClicked={itemClicked} />
            </SmallGrid>
          </span>
        </OmView>
      ) : (
        <div className={styles.galleryFallbackContainer}>
          <GalleryEditButton />
          <img
            data-testid="gallery-preview-fallback-image"
            src={aboutUsImage}
            aria-hidden="true"
            className={styles.defaultImage}
            loading="lazy"
            alt="About us image"
          />
        </div>
      )}
    </>
  );
};

export default GalleryPreview;
